export type ClientCookieOptions = {
	maxAgeSeconds?: number; // omit => session cookie
	sameSite?: "Lax" | "Strict" | "None";
	path?: string;
	domain?: string; // omit => host-only cookie (default browser behavior)
};

/**
 * Single write path for all non-httpOnly first-party cookies.
 * Secure is auto-applied on https so localhost dev keeps working;
 * SameSite=None is rejected without https (browser would drop it).
 */
export function setClientCookie(
	name: string,
	value: string,
	options: ClientCookieOptions = {},
): void {
	const { maxAgeSeconds, sameSite = "Lax", path = "/", domain } = options;
	const secure = window.location.protocol === "https:";
	if (sameSite === "None" && !secure) return;
	const parts = [
		`${name}=${encodeURIComponent(value)}`,
		`Path=${path}`,
		`SameSite=${sameSite}`,
	];
	if (typeof maxAgeSeconds === "number") parts.push(`Max-Age=${maxAgeSeconds}`);
	if (domain) parts.push(`Domain=${domain}`);
	if (secure) parts.push("Secure");
	// biome-ignore lint/suspicious/noDocumentCookie: this IS the single sanctioned write path — every other call site now routes through setClientCookie instead of writing document.cookie directly.
	document.cookie = parts.join("; ");
}

/**
 * Single read path for all non-httpOnly first-party cookies — mirrors
 * {@link setClientCookie}. Returns the decoded value, or `null` if absent.
 */
export function getClientCookie(name: string): string | null {
	const match = document.cookie
		.split("; ")
		.find((c) => c.startsWith(`${name}=`));
	if (!match) return null;
	try {
		return decodeURIComponent(match.slice(name.length + 1));
	} catch {
		return null;
	}
}

export function deleteClientCookie(name: string, path = "/"): void {
	// biome-ignore lint/suspicious/noDocumentCookie: this IS the single sanctioned delete path — every other call site now routes through deleteClientCookie instead of writing document.cookie directly.
	document.cookie = `${name}=; Path=${path}; Max-Age=0; SameSite=Lax`;
}
